import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { Formik } from "formik";
import { Grid, Heading } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppListHeader from "@/features/ui/app-list-header";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useGetDenseProformaInvoice, {
  GetDenseProformaInvoiceParameter,
  GetDenseProformaInvoiceRequest,
} from "@/features/invoices/proforma-invoices/hooks/use-get-dense-proforma-invoice";
import useProformaInvoiceFormValidationSchema from "@/features/invoices/proforma-invoices/hooks/use-proforma-invoice-form-validation-schema";
import { useProformaInvoiceDumbTransaction } from "@/features/invoices/proforma-invoices/hooks/use-proforma-invoice-dumb-transaction";
import useUpdateProformaInvoice, {
  UpdateProformaInvoiceRequest,
} from "@/features/invoices/proforma-invoices/hooks/use-update-proforma-invoice";
import { isFile, isStoredObject, Price } from "@/features/types";
import PriceUtils from "@/features/ui/utils/price-utils";
import {
  CROSS_TRADER_REQUIRED_MESSAGE,
  isCrossTraderMissing,
  proformaRequiredAmounts,
  remainingDepositOf,
  resolveTotalRequiredAmount,
  storedTotalRequiredAmount,
} from "@/features/invoices/proforma-invoices/cross-trade-fee";
import ProformaInvoiceFormModal from "@/features/invoices/proforma-invoices/proforma-invoice-form-modal";
import {
  ProformaInvoiceFormFieldsModal,
} from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page-modal";

interface Props {
  proformaInvoiceId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EditProformaInvoiceModal({ proformaInvoiceId, isOpen, onClose, onSuccess }: Props) {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();
  const { error: showError, warning: showWarning } = useAppToasts();

  const key = useMemo(():
    | { parameter: GetDenseProformaInvoiceParameter; request: GetDenseProformaInvoiceRequest }
    | undefined => {
    if (identity?.company && proformaInvoiceId) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
          proformaInvoiceId,
        },
        request: { skipOrderSheetSets: true },
      };
    }
    return undefined;
  }, [identity, proformaInvoiceId]);

  const {
    data: proformaInvoice,
    error: getProformaInvoiceError,
  } = useGetDenseProformaInvoice(key?.parameter, key?.request, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });


  const initialValues = useMemo((): ProformaInvoiceFormFieldsModal => {
    if (proformaInvoice) {
      // 프로포마 금액 통화 — 서버가 내려준 합계 통화(원화 프로포마가 아니면 유로)
      const currency: Price["currency"] =
        proformaInvoice.totalAmount?.currency === "KRW" ? "KRW" : "EUR";
      // 잔여 디파짓은 OC 헤더 기준 + 이 프로포마가 쓴 디파짓(서버가 되돌린 뒤 다시 깎는다). 브랜드별 디파짓은 더 이상 쓰지 않는다(2026-10-30).
      const orderConfirmationInvoices = proformaInvoice.orderConfirmationInvoices.map(
        (ocInvoice) => {
          const ownUsedDepositOfOc = PriceUtils.sum(
            proformaInvoice.proformaInvoiceDetailList
              .filter((pDetail) =>
                ocInvoice.orderConfirmationInvoiceDetailList.some(
                  (ocDetail) => ocDetail.id === pDetail.orderConfirmationInvoiceDetail?.id
                )
              )
              .map((pDetail) => pDetail.usedDepositAmount),
            currency
          );
          return {
            ...ocInvoice,
            expectedRemainingDepositAmount: PriceUtils.placeholder(
              PriceUtils.sum(
                [ocInvoice.expectedRemainingDepositAmount, ownUsedDepositOfOc],
                currency
              ),
              currency
            ) as Price,
          };
        }
      );
      const expectedRemainingDepositAmount = remainingDepositOf(
        orderConfirmationInvoices,
        currency,
        proformaInvoice.usedDeposit
      );

      // 브랜드 행은 보여 주기만 한다(저장은 서버가 총액 비율로 다시 만든다)
      const clientSideProformaInvoiceDetailList: ProformaInvoiceFormFieldsModal["proformaInvoiceDetailList"] =
        proformaInvoice.proformaInvoiceDetailList.map((pDetail) => ({
          ...pDetail,
          orderConfirmationInvoiceDetail: undefined,
          orderSheetSet: pDetail.orderSheetSet ? { id: pDetail.orderSheetSet.id } : undefined,
        }));

      const values: ProformaInvoiceFormFieldsModal = {
        id: proformaInvoice.id,
        currency,
        name: proformaInvoice.name,
        season: proformaInvoice.season,
        status: proformaInvoice.status,
        lineSheetSetType: proformaInvoice.lineSheetSetType || undefined,
        usedDeposit: proformaInvoice.usedDeposit,
        totalAmount: proformaInvoice.totalAmount,
        orderedBy: proformaInvoice.issuedToCompany,
        isRemittedToBoutique: proformaInvoice.isRemittedToBoutique,
        isRemittedToAgency: proformaInvoice.isRemittedToAgency,
        file: proformaInvoice.file || undefined,
        expectedRemainingDepositAmount,
        issuedThrough: proformaInvoice.issuedThroughCompany,
        issuedBy: proformaInvoice.issuedByCompany,
        originallyIssuedBy: proformaInvoice.originallyIssuedByCompany,
        issuedOn: proformaInvoice.issuedOn,
        paymentMethod: proformaInvoice.paymentMethod,
        paidByAgency: proformaInvoice.paidBy === "AGENCY",
        isBuyerToAgencyEur: proformaInvoice.isBuyerToAgencyEur || false,
        isCrossTrade:
          proformaInvoice.issuedByCompany?.type === "CROSS_TRADER" ||
          !!proformaInvoice.crossTradeFeeRate,
        crossTradeFeeRate: proformaInvoice.crossTradeFeeRate ?? undefined,
        exposedToBuyer: proformaInvoice.isPublic,
        isDetailsEditedManually: proformaInvoice.isDetailsEditedManually || false,
        usedDepositComment: proformaInvoice.usedDepositComment || undefined,
        usedCreditInvoices: proformaInvoice.usedCreditInvoices.map((cInvoice) => ({
          id: cInvoice.id,
          name: cInvoice.name,
          amount: cInvoice.amount,
          season: cInvoice.season,
          ownedByCompany: cInvoice.ownedByCompany,
          issuedToCompany: cInvoice.issuedToCompany,
          issuedByCompany: cInvoice.issuedByCompany,
        })),
        usedCreditComment: proformaInvoice.usedCreditComment || undefined,
        orderConfirmationInvoices,
        orderSheetSets: proformaInvoice.orderSheetSets as any,
        proformaInvoiceDetailList: clientSideProformaInvoiceDetailList,
        childProformaInvoices: proformaInvoice.childProformaInvoices.map((pInvoice) => ({
          id: pInvoice.id,
          orderConfirmationInvoices: pInvoice.orderConfirmationInvoices.map((oc) => ({ id: oc.id })),
          orderSheetSets: pInvoice.orderSheetSets.map((oss) => ({ id: oss.id })),
        })),
        parentProformaInvoices: proformaInvoice.parentProformaInvoices.map((pInvoice) => ({
          id: pInvoice.id,
        })),
        earnedCreditInvoices: proformaInvoice.earnedCreditInvoices.map((ci) => ({ id: ci.id })),
        payments: proformaInvoice.payments.map((p) => ({
          id: p.id,
          status: p.status,
          sender: p.sender,
        })),
        number: proformaInvoice.number || undefined,
        privateComment: proformaInvoice.privateComment || undefined,
        publicComment: proformaInvoice.publicComment || undefined,
        attachments: proformaInvoice.attachments,
        exchangeRate: proformaInvoice.exchangeRate || undefined,
        bankFee: proformaInvoice.bankFee || undefined,
        unoFeeRate:
          proformaInvoice.unoFeeRate !== null && proformaInvoice.unoFeeRate !== undefined
            ? Math.round(proformaInvoice.unoFeeRate * 100)
            : undefined,
        unoFeeExchangeRate: proformaInvoice.unoFeeExchangeRate ?? undefined,
      };
      // 저장된 송금 필요 금액이 자동 계산값과 다르면(담당자가 인보이스에 맞춰 고친 값) 직접 입력값으로 싣는다
      return {
        ...values,
        totalRequiredAmount: storedTotalRequiredAmount(
          proformaInvoice.totalRequiredAmount,
          proformaRequiredAmounts(values, currency).requiredAmount
        ),
      };
    } else {
      return {
        isDetailsEditedManually: false,
        proformaInvoiceDetailList: [],
        orderSheetSets: [],
        orderConfirmationInvoices: [],
        usedCreditInvoices: [],
        exposedToBuyer: true,
        paidByAgency: true,
        issuedOn: new Date(),
        childProformaInvoices: [],
        parentProformaInvoices: [],
        earnedCreditInvoices: [],
        payments: [],
        attachments: [],
      };
    }
  }, [proformaInvoice]);

  const validateSchema = useProformaInvoiceFormValidationSchema();
  const validate = useCallback(
    async (values: ProformaInvoiceFormFieldsModal) => {
      try {
        await validateSchema.validate(values, { abortEarly: false });
      } catch (e) {
        showError(e);
        return e;
      }
      if (isCrossTraderMissing(values)) {
        showWarning(CROSS_TRADER_REQUIRED_MESSAGE);
        return { issuedBy: CROSS_TRADER_REQUIRED_MESSAGE };
      }
    },
    [validateSchema, showError, showWarning]
  );

  const { addUpdate } = useProformaInvoiceDumbTransaction();
  const { fire: fireUpdateProformaInvoice } = useUpdateProformaInvoice();

  const toRequest = useCallback(
    (values: ProformaInvoiceFormFieldsModal) => {
      const currency = values.currency ?? "EUR";
      const totalUsedCredit = PriceUtils.sum(
        values.usedCreditInvoices.map((ocInvoice) => ocInvoice.amount),
        currency
      );
      // 브랜드별 금액은 보내지 않는다 — 서버가 총액·사용 디파짓을 연결 오더·OC 비율로 나눠 저장한다(2026-10-30)
      const { amount: totalAmount, requiredAmount } = proformaRequiredAmounts(values, currency);

      return {
        id: values.id!,
        number: values.number!,
        season: values.season!,
        name: values.name!,
        status: values.status!,
        lineSheetSetType: values.lineSheetSetType || null,
        issuedToCompany: values.orderedBy!,
        issuedByCompany: values.issuedBy!,
        issuedThroughCompany: values.issuedThrough!,
        issuedOn: values.issuedOn!,
        paymentMethod: values.paymentMethod!,
        paidBy: values.paidByAgency ? ("AGENCY" as const) : ("BUYER" as const),
        isBuyerToAgencyEur: values.paidByAgency && !!values.isBuyerToAgencyEur,
        isPublic: values.exposedToBuyer,
        publicComment: values.publicComment || null,
        privateComment: values.privateComment || null,
        usedCreditInvoices: values.usedCreditInvoices.map((cInvoice) => ({ id: cInvoice.id })),
        orderSheetSets: values.orderSheetSets.map((oss) => ({ id: oss.id })),
        orderConfirmationInvoices: values.orderConfirmationInvoices.map((ocInvoice) => ({
          id: ocInvoice.id,
        })),
        usedDeposit: PriceUtils.placeholder(values.usedDeposit, currency),
        usedDepositComment: values.usedDepositComment || null,
        usedCredit: PriceUtils.placeholder(totalUsedCredit, currency),
        usedCreditComment: values.usedCreditComment || null,
        isDetailsEditedManually: false,
        totalAmount: PriceUtils.placeholder(totalAmount, currency),
        totalRequiredAmount: PriceUtils.placeholder(
          resolveTotalRequiredAmount(values.totalRequiredAmount, requiredAmount),
          currency
        ),
        // 3자무역 수수료율은 더 이상 쓰지 않는다 — 0 을 보내 서버 값도 지운다
        crossTradeFeeRate: 0,
        proformaInvoiceDetailList: [],
        originallyIssuedByCompany: values.originallyIssuedBy!,
        childProformaInvoices: values.childProformaInvoices.map((pInvoice) => ({ id: pInvoice.id })),
        file: values.file!,
        attachments: values.attachments,
        exchangeRate: values.exchangeRate || null,
        bankFee: PriceUtils.placeholder(values.bankFee, "KRW"),
        unoFeeRate:
          values.unoFeeRate !== undefined && values.unoFeeRate !== null
            ? values.unoFeeRate / 100
            : null,
        unoFeeExchangeRate: values.unoFeeExchangeRate ?? null,
      };
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: ProformaInvoiceFormFieldsModal) => {
      if (!identity?.company || !values.id) return false;

      const parameter = {
        by: identity.company.type,
        companyId: identity.company.id,
        proformaInvoiceId: values.id,
      };

      const request = toRequest(values);
      const isSmart =
        request.file && isStoredObject(request.file) &&
        (request.attachments as any[]).every((a) => isStoredObject(a) || !isFile(a));

      try {
        if (isSmart) {
          await fireUpdateProformaInvoice(parameter, request as unknown as UpdateProformaInvoiceRequest);
        } else {
          await addUpdate(parameter, request as any);
        }
        onSuccess?.();
        onClose();
      } catch (e) {
        showError(e);
        return false;
      }

      return new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(true), 500);
      });
    },
    [identity, toRequest, fireUpdateProformaInvoice, addUpdate, onSuccess, onClose, showError]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"} overflow={"hidden"} maxWidth={"1600px"}>
        <ModalBody display="flex" flexDirection={"column"} gap={"12px"}>
          <Formik<ProformaInvoiceFormFieldsModal>
            enableReinitialize={true}
            initialValues={initialValues}
            validate={validate}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={(values) => handleSubmit(values)}
          >
            {(props) => (
              <Grid
                as={"form"}
                height={"100%"}
                width={"100%"}
                gridTemplateColumns={"1fr"}
                gridTemplateRows={"auto auto 1fr"}
              >
                <AppListHeader
                  headers={[
                    <Heading key={"EditProformaInvoice"} as={"h2"} fontSize={"14px"}>
                      {tTitle("proforma_invoices:upload_proforma_invoice")}
                    </Heading>,
                  ]}
                  filters={[]}
                  actions={[
                    <AppButton
                      key="Cancel"
                      isDisabled={props.isSubmitting}
                      onClick={onClose}
                    >
                      {tTitle("cancel")}
                    </AppButton>,
                    <AppButton
                      key="Save"
                      isDisabled={props.isSubmitting}
                      isLoading={props.isSubmitting}
                      onClick={() => props.submitForm()}
                    >
                      {tTitle("save")}
                    </AppButton>,
                  ]}
                />
                <ProformaInvoiceFormModal
                  formik={props}
                  isEditing={true}
                  payments={proformaInvoice?.payments}
                  // 시즌 프로포마는 오더 대신 OC 연결·디파짓을 보여 준다(업로드 모달과 같다)
                  isSeason={proformaInvoice?.lineSheetSetType === "SEASON"}
                />
              </Grid>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
